import { Fragment, type ReactNode } from 'react';
import { Box, ListItem, Text, UnorderedList } from '@chakra-ui/react';
import { overflowScrollbarStyles } from '../../styles/overflowScrollbarStyles';
import { pxToRem } from '../../constants/scalable';

type Section<T> = { key: string; label: string; items: T[] };

export type SectionedListProps<T> = {
  items: T[];
  getItemKey: (item: T) => string;
  // Derives the section an item belongs to. Items are grouped in the order given,
  // so the caller is responsible for sorting items by section beforehand.
  getSection: (item: T) => { key: string; label: string };
  renderItem: (item: T) => ReactNode;
  onSelectItem?: (item: T) => void;
  isItemSelected?: (item: T) => boolean;
  isItemDisabled?: (item: T) => boolean;
  emptyState?: ReactNode;
  maxHeight?: string;
};

// Groups an already-sorted list into contiguous sections so a heading can be rendered
// once per section. Exported for unit testing.
export const groupItemsIntoSections = <T,>(
  items: T[],
  getSection: (item: T) => { key: string; label: string },
): Array<Section<T>> => {
  const sections: Array<Section<T>> = [];
  for (const item of items) {
    const section = getSection(item);
    const current = sections[sections.length - 1];
    if (current == null || current.key !== section.key) {
      sections.push({ ...section, items: [item] });
    } else {
      current.items.push(item);
    }
  }
  return sections;
};

export function SectionedList<T>({
  items,
  getItemKey,
  getSection,
  renderItem,
  onSelectItem,
  isItemSelected,
  isItemDisabled,
  emptyState,
  maxHeight = pxToRem(450),
}: SectionedListProps<T>) {
  const sections = groupItemsIntoSections(items, getSection);

  if (sections.length === 0) {
    return emptyState ?? null;
  }

  return (
    <Box maxH={maxHeight} overflowY="auto" w="full" pr={2} css={overflowScrollbarStyles}>
      <UnorderedList
        styleType="none"
        w="full"
        p="0"
        m="0"
        border="1px solid"
        borderRadius="md"
        borderColor="border.emphasis"
        sx={{
          'li:not(:last-child)': {
            borderBottom: '1px solid',
            borderColor: 'border.emphasis',
          },
          'li:first-of-type': {
            borderTopLeftRadius: 'md',
            borderTopRightRadius: 'md',
          },
          'li:last-of-type': {
            borderBottomLeftRadius: 'md',
            borderBottomRightRadius: 'md',
          },
        }}
      >
        {sections.map((section) => (
          <Fragment key={section.key}>
            <ListItem px={4} py={2} bg="bg.muted" position="sticky" top="0" zIndex={1}>
              <Text
                fontSize="xs"
                fontWeight="bold"
                color="fg.muted"
                textTransform="uppercase"
                letterSpacing="wide"
              >
                {section.label}
              </Text>
            </ListItem>
            {section.items.map((item) => {
              const disabled = isItemDisabled?.(item) ?? false;
              const selected = isItemSelected?.(item) ?? false;
              const handleClick =
                onSelectItem != null && !disabled ? () => onSelectItem(item) : undefined;

              return (
                <ListItem
                  key={getItemKey(item)}
                  onClick={handleClick}
                  px={4}
                  py={2}
                  bg={disabled ? 'bg.subtle' : selected ? 'bg.muted' : 'transparent'}
                  color={disabled ? 'fg.disabled' : 'inherit'}
                  cursor={disabled ? 'not-allowed' : handleClick != null ? 'pointer' : 'default'}
                  pointerEvents={disabled ? 'none' : 'auto'}
                  opacity={disabled ? 0.6 : 1}
                  _hover={
                    handleClick != null ? { bg: selected ? 'bg.muted' : 'bg.subtle' } : undefined
                  }
                >
                  {renderItem(item)}
                </ListItem>
              );
            })}
          </Fragment>
        ))}
      </UnorderedList>
    </Box>
  );
}
